import { Link } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { useSeo } from "@/hooks/useSeo";
import SectionHeading from "@/components/base/SectionHeading";
import AnimeGrid from "@/components/feature/AnimeGrid";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuick from "@/pages/home/components/CategoryQuick";
import { formatViews } from "@/utils/format";

export default function Home() {
  useSeo({
    title: "樱花动漫网站－热门动漫资源在线观看平台",
    description:
      "樱花动漫网站提供丰富的热门动漫资源，涵盖最新动画和经典动漫作品，满足用户动漫在线观看和资源查找需求。",
    keywords: "樱花动漫网站,动漫在线观看,热门动漫,动画资源,动漫视频,原创动漫,国内动漫,动漫推荐",
  });

  const featured = animeList.filter((item) => item.featured);
  const latest = [...animeList].sort((a, b) => b.year - a.year).slice(0, 10);
  const hottest = [...animeList].sort((a, b) => b.views - a.views);
  const topRanking = hottest.slice(0, 8);
  const hotGrid = hottest.slice(0, 10);

  return (
    <div className="mx-auto px-4 py-6 md:px-8 md:py-8">
      <h1 className="sr-only">樱花动漫网站 － 热门动漫资源在线观看与信息查询平台</h1>

      <HeroCarousel items={featured} />

      <section className="mt-10 md:mt-14">
        <SectionHeading
          eyebrow="快速入口"
          title="按类型发现好番"
          description="热血、恋爱、冒险、治愈……挑一个你现在的心情，直接进入对应分类，快速锁定想看的作品。"
          actionText="查看全部分类"
          actionTo="/category"
        />
        <CategoryQuick />
      </section>

      <section className="mt-10 md:mt-16">
        <SectionHeading
          eyebrow="每周更新"
          title="最新动漫推荐"
          description="紧跟每季新番节奏，收录近期上线的热门动画与连载作品，第一时间追到最新剧情。"
          actionText="查看更多"
          actionTo="/category?sort=latest"
        />
        <AnimeGrid items={latest} columns={5} />
      </section>

      <section className="mt-10 md:mt-16">
        <SectionHeading
          eyebrow="播放量排行"
          title="热门动漫精选"
          description="按站内播放量统计的热门作品，看看大家都在追什么。"
          actionText="进入排行榜"
          actionTo="/category?sort=hot"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200/70 bg-primary-50/50 p-5 lg:col-span-1">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-bar-chart-2-line text-primary-600" />
              热播榜单 TOP 8
            </h3>
            <ol className="mt-4 space-y-1">
              {topRanking.map((anime, index) => (
                <li key={anime.id}>
                  <Link
                    to={`/detail/${anime.id}`}
                    className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-50"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        index < 3
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground-900">
                        {anime.title}
                      </span>
                      <span className="block text-xs text-foreground-500">
                        {anime.genres.slice(0, 2).join(" / ")}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-play-circle-line text-primary-500" />
                      {formatViews(anime.views)}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="lg:col-span-2">
            <AnimeGrid items={hotGrid} columns={4} />
          </div>
        </div>
      </section>

      <section className="mt-10 md:mt-16" aria-label="网站简介">
        <div className="relative overflow-hidden rounded-lg bg-primary-500 p-6 md:p-10">
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
                关于樱花动漫网站
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-50/90 md:text-base">
                我们专注于热门动漫资源的在线观看与信息展示，收录最新动画与经典动漫作品。
                从类型、地区到年份，多维度筛选帮你快速定位想看的内容，收藏你喜欢的作品，打造属于你的追番清单。
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                了解我们
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-search-line" />
                搜索动漫
              </Link>
            </div>
          </div>
          <i className="ri-flower-fill pointer-events-none absolute -bottom-8 right-4 text-[140px] leading-none text-background-50/10" />
        </div>
      </section>
    </div>
  );
}